import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Camera,
  CameraOff,
  Video,
  VideoOff,
  ShieldCheck,
  Lock,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Info,
  X,
  Mic,
  MicOff,
  RefreshCw,
  Eye,
  Smile,
  Check,
  ChevronRight,
  Sliders,
  Settings
} from "lucide-react";
import { toast } from "sonner";
import { useAppSelector } from "../../store/hooks";

interface CameraPermissionModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  isFirstSignup?: boolean;
}

export function CameraPermissionModal({
  isOpen: externalIsOpen,
  onClose: externalOnClose,
  isFirstSignup = false,
}: CameraPermissionModalProps) {
  const { user } = useAppSelector((state) => state.auth);
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [permissionState, setPermissionState] = useState<"idle" | "requesting" | "granted" | "denied" | "unsupported">("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [micActive, setMicActive] = useState<boolean>(true);
  const [videoActive, setVideoActive] = useState<boolean>(true);
  const [acknowledgedNotes, setAcknowledgedNotes] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<"camera" | "notes" | "privacy">("camera");

  const videoRef = useRef<HTMLVideoElement | null>(null);

  const storageKey = user ? `feeltalk_camera_permission_seen_${user.id || user._id || user.email}` : "feeltalk_camera_permission_seen_guest";

  // Check if should auto-open on first signup
  useEffect(() => {
    if (externalIsOpen !== undefined) {
      setInternalIsOpen(externalIsOpen);
      return;
    }

    if (user) {
      const alreadySeen = localStorage.getItem(storageKey);
      if (!alreadySeen) {
        setInternalIsOpen(true);
      }
    }
  }, [user, externalIsOpen, storageKey]);

  // Listen to global trigger event so any component can open this modal anytime
  useEffect(() => {
    const handleGlobalOpen = () => setInternalIsOpen(true);
    window.addEventListener("open-camera-permission-modal", handleGlobalOpen);
    return () => {
      window.removeEventListener("open-camera-permission-modal", handleGlobalOpen);
    };
  }, []);

  // Bind video stream to video element
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream, internalIsOpen, activeTab]);

  // Clean up media stream on close or unmount
  useEffect(() => {
    return () => {
      stopCameraStream();
    };
  }, []);

  const stopCameraStream = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const requestCameraPermission = async () => {
    setPermissionState("requesting");
    setErrorMessage("");

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setPermissionState("unsupported");
      setErrorMessage("Your browser does not support media device camera access.");
      toast.error("Camera unsupported on this browser");
      return;
    }

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
        audio: true,
      });

      setStream(mediaStream);
      setPermissionState("granted");
      setVideoActive(true);
      setMicActive(true);
      toast.success("Camera & Microphone access granted!", {
        description: "Your video & audio preview is live.",
      });
    } catch (err: any) {
      console.error("Camera permission error:", err);
      setPermissionState("denied");
      stopCameraStream();
      
      let msg = "Camera permission was denied or blocked.";
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        msg = "Camera access was denied by your browser. Please allow camera permissions in your browser address bar settings.";
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        msg = "No camera hardware device found connected to your device.";
      } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
        msg = "Your camera is currently being used by another application.";
      }
      setErrorMessage(msg);
      toast.error("Camera Access Needed", { description: msg });
    }
  };

  const toggleVideoTrack = () => {
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setVideoActive(videoTrack.enabled);
      }
    }
  };

  const toggleAudioTrack = () => {
    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setMicActive(audioTrack.enabled);
      }
    }
  };

  const handleFinishSetup = () => {
    localStorage.setItem(storageKey, "true");
    stopCameraStream();
    setInternalIsOpen(false);
    if (externalOnClose) externalOnClose();
    toast.success("Permissions saved!", {
      description: "Welcome to FeelTalk! You are ready for video calls & emotion tracking.",
    });
  };

  const handleSkip = () => {
    localStorage.setItem(storageKey, "skipped");
    stopCameraStream();
    setInternalIsOpen(false);
    if (externalOnClose) externalOnClose();
    toast.info("Camera setup skipped", {
      description: "You can enable camera permission anytime in video calls or profile settings.",
    });
  };

  if (!internalIsOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-2.5 sm:p-4 bg-slate-900/70 backdrop-blur-md overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.3 }}
          className="relative w-full max-w-2xl max-h-[92vh] sm:max-h-[85vh] flex flex-col bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-purple-100 overflow-hidden my-auto"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-700 via-pink-600 to-indigo-700 text-white p-4 sm:p-6 relative flex-shrink-0">
            <button
              onClick={handleSkip}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-all"
              title="Close"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <div className="flex items-center gap-2.5 sm:gap-3 mb-1.5 sm:mb-2 pr-6 sm:pr-0">
              <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-md rounded-xl sm:rounded-2xl text-white shadow-inner flex-shrink-0">
                <Camera className="w-5 h-5 sm:w-7 sm:h-7 animate-pulse" />
              </div>
              <div className="min-w-0">
                <span className="inline-block px-2.5 py-0.5 sm:px-3 sm:py-1 bg-white/20 backdrop-blur-md rounded-full text-[10px] sm:text-xs font-semibold uppercase tracking-wider mb-0.5 sm:mb-1 truncate max-w-full">
                  {isFirstSignup ? "Welcome to FeelTalk • Initial Setup" : "Camera & Device Permissions"}
                </span>
                <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight leading-tight">
                  Camera Access & Guidelines
                </h2>
              </div>
            </div>
            <p className="text-purple-100 text-xs sm:text-sm mt-1 sm:mt-2 max-w-xl">
              Enable your camera & microphone for 1-on-1 video support sessions, live emotion AI mood insights, and identity safety.
            </p>
          </div>

          {/* Navigation Tabs */}
          <div className="flex border-b border-gray-100 bg-gray-50/80 px-3 sm:px-6 pt-2 sm:pt-3 gap-1.5 sm:gap-2 overflow-x-auto flex-shrink-0 scrollbar-none">
            <button
              onClick={() => setActiveTab("camera")}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 font-medium text-xs sm:text-sm rounded-t-xl transition-all border-b-2 whitespace-nowrap ${
                activeTab === "camera"
                  ? "border-purple-600 text-purple-700 bg-white shadow-sm font-semibold"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              <Video className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              <span>Camera <span className="hidden sm:inline">Test & Live Feed</span><span className="sm:hidden">Test</span></span>
            </button>
            <button
              onClick={() => setActiveTab("notes")}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 font-medium text-xs sm:text-sm rounded-t-xl transition-all border-b-2 whitespace-nowrap ${
                activeTab === "notes"
                  ? "border-purple-600 text-purple-700 bg-white shadow-sm font-semibold"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              <Info className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              <span>Notes <span className="hidden sm:inline">& Rules</span></span>
            </button>
            <button
              onClick={() => setActiveTab("privacy")}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 font-medium text-xs sm:text-sm rounded-t-xl transition-all border-b-2 whitespace-nowrap ${
                activeTab === "privacy"
                  ? "border-purple-600 text-purple-700 bg-white shadow-sm font-semibold"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              <span>Privacy <span className="hidden sm:inline">& Safeguards</span></span>
            </button>
          </div>

          {/* Body Content */}
          <div className="p-4 sm:p-6 flex-1 overflow-y-auto min-h-0 space-y-4 sm:space-y-6">
            {/* TAB 1: Camera Test */}
            {activeTab === "camera" && (
              <div className="space-y-4 sm:space-y-6">
                <div className="relative bg-slate-900 rounded-xl sm:rounded-2xl overflow-hidden aspect-video border-2 border-purple-200 shadow-inner flex items-center justify-center min-h-[170px] sm:min-h-[220px]">
                  {permissionState === "granted" && stream ? (
                    <>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className={`w-full h-full object-cover transform -scale-x-100 ${
                          !videoActive ? "hidden" : "block"
                        }`}
                      />
                      {!videoActive && (
                        <div className="flex flex-col items-center justify-center text-slate-400 p-4 sm:p-6 text-center">
                          <CameraOff className="w-8 h-8 sm:w-12 sm:h-12 mb-2 text-slate-500" />
                          <p className="font-medium text-xs sm:text-sm text-slate-300">Camera preview paused</p>
                          <p className="text-[10px] sm:text-xs text-slate-500">Video track is temporarily disabled</p>
                        </div>
                      )}

                      {/* Video Overlay Status Badge */}
                      <div className="absolute top-2 left-2 sm:top-4 sm:left-4 flex items-center gap-1.5 sm:gap-2 bg-slate-900/80 backdrop-blur-md px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-semibold text-emerald-400 border border-emerald-500/30">
                        <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-emerald-500 animate-ping" />
                        Camera Active & Ready
                      </div>

                      {/* Video Controls Bar */}
                      <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 sm:gap-3 bg-slate-900/90 backdrop-blur-md px-3 py-1.5 sm:px-4 sm:py-2 rounded-full border border-slate-700 shadow-lg">
                        <button
                          onClick={toggleVideoTrack}
                          className={`p-2 sm:p-2.5 rounded-full transition-all ${
                            videoActive
                              ? "bg-purple-600 text-white hover:bg-purple-700"
                              : "bg-red-600 text-white hover:bg-red-700"
                          }`}
                          title={videoActive ? "Turn Off Video" : "Turn On Video"}
                        >
                          {videoActive ? <Video className="w-4 h-4 sm:w-5 sm:h-5" /> : <VideoOff className="w-4 h-4 sm:w-5 sm:h-5" />}
                        </button>
                        <button
                          onClick={toggleAudioTrack}
                          className={`p-2 sm:p-2.5 rounded-full transition-all ${
                            micActive
                              ? "bg-purple-600 text-white hover:bg-purple-700"
                              : "bg-red-600 text-white hover:bg-red-700"
                          }`}
                          title={micActive ? "Mute Microphone" : "Unmute Microphone"}
                        >
                          {micActive ? <Mic className="w-4 h-4 sm:w-5 sm:h-5" /> : <MicOff className="w-4 h-4 sm:w-5 sm:h-5" />}
                        </button>
                      </div>
                    </>
                  ) : permissionState === "requesting" ? (
                    <div className="text-center p-4 sm:p-8">
                      <RefreshCw className="w-8 h-8 sm:w-12 sm:h-12 text-purple-400 animate-spin mx-auto mb-2 sm:mb-3" />
                      <p className="text-white font-semibold text-base sm:text-lg">Requesting Camera Access...</p>
                      <p className="text-purple-200 text-[11px] sm:text-xs mt-1">Please allow camera permissions in your browser prompt.</p>
                    </div>
                  ) : permissionState === "denied" ? (
                    <div className="text-center p-4 sm:p-6 max-w-md">
                      <AlertCircle className="w-8 h-8 sm:w-12 sm:h-12 text-rose-500 mx-auto mb-2 sm:mb-3" />
                      <p className="text-white font-semibold text-sm sm:text-base">Camera Access Blocked</p>
                      <p className="text-rose-200 text-[11px] sm:text-xs mt-1">{errorMessage}</p>
                      <div className="mt-3 p-2.5 sm:p-3 bg-slate-800/80 rounded-xl text-left text-[11px] sm:text-xs text-slate-300 space-y-1 border border-slate-700">
                        <p className="font-semibold text-amber-400 mb-0.5">💡 How to enable in browser:</p>
                        <p>1. Click the 🔒 lock icon near the URL bar above.</p>
                        <p>2. Toggle <strong>Camera</strong> to <strong>"Allow"</strong>.</p>
                        <p>3. Click "Retry Camera Test" below.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center p-4 sm:p-8">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 border border-purple-400/30">
                        <Camera className="w-6 h-6 sm:w-8 sm:h-8 text-purple-300" />
                      </div>
                      <p className="text-white font-semibold text-base sm:text-lg">Test Your Video & Camera Feed</p>
                      <p className="text-slate-400 text-xs mt-1 max-w-sm mx-auto px-2">
                        Click the button below to grant permission and verify your camera display before entering live calls.
                      </p>
                      <button
                        onClick={requestCameraPermission}
                        className="mt-4 sm:mt-5 inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-xs sm:text-sm font-semibold rounded-xl sm:rounded-2xl shadow-lg shadow-purple-500/30 transition-all transform hover:-translate-y-0.5 active:scale-95"
                      >
                        <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
                        Allow & Test Camera Now
                      </button>
                    </div>
                  )}
                </div>

                {permissionState === "granted" && (
                  <div className="p-3.5 sm:p-4 bg-emerald-50 border border-emerald-200 rounded-xl sm:rounded-2xl flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 flex-shrink-0" />
                    <div>
                      <h4 className="text-xs sm:text-sm font-bold text-emerald-900">Camera & Microphone Working Great!</h4>
                      <p className="text-[11px] sm:text-xs text-emerald-700">
                        Your device is fully configured for video sessions and emotion tracking.
                      </p>
                    </div>
                  </div>
                )}

                {permissionState === "denied" && (
                  <div className="flex justify-center">
                    <button
                      onClick={requestCameraPermission}
                      className="px-4 py-2 sm:px-5 sm:py-2.5 bg-slate-800 text-white font-medium rounded-xl hover:bg-slate-700 transition-all flex items-center gap-2 text-xs sm:text-sm"
                    >
                      <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      Retry Camera Test
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* TAB 2: Notes & Rules */}
            {activeTab === "notes" && (
              <div className="space-y-3.5 sm:space-y-4">
                <div className="bg-purple-50 border border-purple-100 rounded-xl sm:rounded-2xl p-3.5 sm:p-4">
                  <h3 className="font-bold text-purple-900 text-sm sm:text-base mb-1 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
                    Why Camera Permissions are Required in FeelTalk
                  </h3>
                  <p className="text-purple-700 text-xs sm:text-sm">
                    FeelTalk brings authentic human empathy to digital conversations. Here are the core features powered by camera access:
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                  <div className="p-3.5 sm:p-4 bg-gray-50 rounded-xl sm:rounded-2xl border border-gray-100 hover:border-purple-200 transition-all">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-purple-100 text-purple-600 rounded-lg sm:rounded-xl flex items-center justify-center font-bold text-xs sm:text-sm mb-1.5 sm:mb-2">
                      1
                    </div>
                    <h4 className="font-bold text-gray-900 text-xs sm:text-sm mb-1">1-on-1 Video Support Sessions</h4>
                    <p className="text-[11px] sm:text-xs text-gray-600">
                      Engage face-to-face with vetted, caring listeners or peer supporters to share your thoughts comfortably.
                    </p>
                  </div>

                  <div className="p-3.5 sm:p-4 bg-gray-50 rounded-xl sm:rounded-2xl border border-gray-100 hover:border-purple-200 transition-all">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-pink-100 text-pink-600 rounded-lg sm:rounded-xl flex items-center justify-center font-bold text-xs sm:text-sm mb-1.5 sm:mb-2">
                      2
                    </div>
                    <h4 className="font-bold text-gray-900 text-xs sm:text-sm mb-1">Live Emotion AI & Facial Sentiment</h4>
                    <p className="text-[11px] sm:text-xs text-gray-600">
                      Optional real-time facial mood detection to offer targeted wellness advice and track emotional progress.
                    </p>
                  </div>

                  <div className="p-3.5 sm:p-4 bg-gray-50 rounded-xl sm:rounded-2xl border border-gray-100 hover:border-purple-200 transition-all">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-indigo-100 text-indigo-600 rounded-lg sm:rounded-xl flex items-center justify-center font-bold text-xs sm:text-sm mb-1.5 sm:mb-2">
                      3
                    </div>
                    <h4 className="font-bold text-gray-900 text-xs sm:text-sm mb-1">Identity & Trust Verification</h4>
                    <p className="text-[11px] sm:text-xs text-gray-600">
                      Listeners and users complete live selfie checks to prevent impersonation and guarantee community safety.
                    </p>
                  </div>

                  <div className="p-3.5 sm:p-4 bg-gray-50 rounded-xl sm:rounded-2xl border border-gray-100 hover:border-purple-200 transition-all">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-emerald-100 text-emerald-600 rounded-lg sm:rounded-xl flex items-center justify-center font-bold text-xs sm:text-sm mb-1.5 sm:mb-2">
                      4
                    </div>
                    <h4 className="font-bold text-gray-900 text-xs sm:text-sm mb-1">Full Control During Calls</h4>
                    <p className="text-[11px] sm:text-xs text-gray-600">
                      Toggle video or switch to audio-only mode at any moment during a call with a single tap.
                    </p>
                  </div>
                </div>

                <div className="p-3.5 sm:p-4 bg-amber-50 border border-amber-200 rounded-xl sm:rounded-2xl text-[11px] sm:text-xs text-amber-800 space-y-1">
                  <p className="font-bold text-amber-900 flex items-center gap-1.5 mb-1">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    Recommended Environment for Best Experience:
                  </p>
                  <p>• Sit in a quiet room with good forward lighting so your face is clearly visible.</p>
                  <p>• Use headphones/earbuds for clear audio and privacy during sensitive discussions.</p>
                  <p>• Respect Community Guidelines: Harassment or inappropriate video conduct is strictly prohibited.</p>
                </div>
              </div>
            )}

            {/* TAB 3: Privacy & Safeguards */}
            {activeTab === "privacy" && (
              <div className="space-y-3.5 sm:space-y-4">
                <div className="p-3.5 sm:p-4 bg-slate-900 text-white rounded-xl sm:rounded-2xl space-y-1.5 sm:space-y-2">
                  <div className="flex items-center gap-2 text-emerald-400 font-bold text-xs sm:text-sm">
                    <Lock className="w-4 h-4 flex-shrink-0" />
                    End-to-End Encryption & Zero Storage
                  </div>
                  <p className="text-slate-300 text-[11px] sm:text-xs leading-relaxed">
                    At FeelTalk, your privacy is our highest priority. Your video stream is directly connected peer-to-peer (WebRTC) using bank-grade encryption. We <strong>NEVER</strong> record, store, or monitor video calls.
                  </p>
                </div>

                <div className="space-y-2.5 sm:space-y-3">
                  <div className="flex items-start gap-2.5 sm:gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h5 className="text-xs sm:text-sm font-semibold text-gray-900">No Automatic Video Recording</h5>
                      <p className="text-[11px] sm:text-xs text-gray-600">
                        No call recordings are ever saved on servers. Your conversation stays between you and your listener.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2.5 sm:gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <VideoOff className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h5 className="text-xs sm:text-sm font-semibold text-gray-900">Instant Privacy Mute</h5>
                      <p className="text-[11px] sm:text-xs text-gray-600">
                        Turn off your camera or microphone instantly whenever you need privacy during an active session.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2.5 sm:gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h5 className="text-xs sm:text-sm font-semibold text-gray-900">Selective Permission Control</h5>
                      <p className="text-[11px] sm:text-xs text-gray-600">
                        You can revoke camera access at any time through your browser settings or profile controls.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Checkbox Acknowledgment */}
            <div className="pt-3.5 sm:pt-4 border-t border-gray-100">
              <label className="flex items-start gap-2.5 sm:gap-3 cursor-pointer p-2.5 sm:p-3 bg-purple-50/60 rounded-xl hover:bg-purple-50 transition-all border border-purple-100">
                <input
                  type="checkbox"
                  checked={acknowledgedNotes}
                  onChange={(e) => setAcknowledgedNotes(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 flex-shrink-0"
                />
                <span className="text-xs sm:text-sm text-gray-700 leading-snug">
                  I have read the camera usage notes and agree to maintain respectful, safe video conduct according to{" "}
                  <strong className="text-purple-700">FeelTalk Community Guidelines</strong>.
                </span>
              </label>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-3.5 sm:p-5 bg-gray-50 border-t border-gray-100 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-2.5 sm:gap-4 flex-shrink-0">
            <button
              onClick={handleSkip}
              className="w-full sm:w-auto px-4 py-2 sm:px-5 sm:py-2.5 text-gray-500 hover:text-gray-900 font-medium text-xs sm:text-sm transition-colors rounded-xl border border-gray-200 hover:bg-gray-100 text-center order-1 sm:order-1"
            >
              Skip for Now
            </button>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto order-2 sm:order-2">
              {permissionState !== "granted" && (
                <button
                  onClick={requestCameraPermission}
                  className="w-full sm:w-auto px-4 py-2.5 bg-purple-100 text-purple-700 hover:bg-purple-200 font-semibold text-xs sm:text-sm rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  <Camera className="w-4 h-4 flex-shrink-0" />
                  Grant Permission
                </button>
              )}

              <button
                onClick={handleFinishSetup}
                disabled={!acknowledgedNotes}
                className={`w-full sm:w-auto px-5 sm:px-6 py-2.5 font-semibold text-xs sm:text-sm rounded-xl transition-all shadow-md flex items-center justify-center gap-2 ${
                  acknowledgedNotes
                    ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-purple-500/20"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Complete Setup & Continue
                <ChevronRight className="w-4 h-4 flex-shrink-0" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
export default CameraPermissionModal;
